#cf-class-player,
#cf-recordings {
  /* --- palette: deep pine ground, chalk type, broadcast red reserved for live --- */
  --ink: #0d1512;
  --ink-soft: #16201c;
  --line: #2a3733;
  --chalk: #eef2ef;
  --chalk-dim: #94a49d;
  --signal: #e03d2f;
  --focus: #7fd4b8;

  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  color: var(--chalk);
  max-width: 62rem;
  margin: 0 auto;
  -webkit-text-size-adjust: 100%;
}

#cf-class-player *,
#cf-recordings * { box-sizing: border-box; }

.cfp-browser-banner {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  margin-bottom: 0.75rem;
  padding: 0.75rem 1rem;
  background: var(--focus);
  color: #06110d;
  border-radius: 10px;
  text-decoration: none;
}

.cfp-browser-banner-head {
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.cfp-browser-banner-body {
  font-size: 0.82rem;
  line-height: 1.4;
  color: #0d2b22;
}

.cfp-browser-banner:hover,
.cfp-browser-banner:focus-visible {
  filter: brightness(1.05);
}

.cfp-frame {
  background: var(--ink);
  border: 1px solid var(--line);
  border-radius: 10px;
  overflow: hidden;
}

.cfp-stage { position: relative; background: #000; }

.cfp-stage .video-js { width: 100%; }

/* Our own .cfp-overlay already covers every status (waiting/blocked/ended/
   sign-in-required/etc.) — video.js's own built-in error-display component
   would otherwise also pop up "The media could not be loaded" on top of it.
   Suppressing it via CSS, not the `children` player option — passing
   `children: { errorDisplay: false }` to videojs() was tried first and
   confirmed live to silently break the player's ENTIRE component tree
   (control bar, big play button, everything gone, only the raw <video> tag
   left) — a real, serious regression, not a safe way to hide one child. */
.cfp-stage .vjs-error-display,
#cf-recordings .vjs-error-display { display: none !important; }

/* In real fullscreen, the video-js root fills the device's actual screen,
   which on modern phones includes notches, camera cutouts, rounded
   corners, and the home-indicator bar — none of which the control bar
   otherwise avoids, so buttons in its corners (confirmed: the pause
   button) get physically clipped by the hardware/OS chrome. Padding the
   bar by the safe-area insets pulls every control inward from those
   edges. Requires viewport-fit=cover on the page's own viewport meta tag
   or these all resolve to 0 (set on the standalone page's index.html;
   inside the Kajabi embed this depends on Kajabi's own page meta, outside
   our control). */
.video-js:fullscreen .vjs-control-bar,
.video-js.vjs-fullscreen .vjs-control-bar {
  padding-left: max(env(safe-area-inset-left), 0px) !important;
  padding-right: max(env(safe-area-inset-right), 0px) !important;
  padding-bottom: max(env(safe-area-inset-bottom), 0px) !important;
  box-sizing: border-box !important;
}

.cfp-overlay[hidden] {
  display: none;
}

.cfp-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  text-align: center;
  padding: 1.5rem;
  background: var(--ink);
  z-index: 3;
}

.cfp-overlay-head {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.cfp-overlay-body {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--chalk-dim);
  max-width: 34ch;
}

.cfp-overlay-refresh {
  margin-top: 0.3rem;
  padding: 0.5rem 1.1rem;
  border: 1px solid var(--line);
  border-radius: 6px;
  background: transparent;
  color: var(--chalk);
  font-size: 0.85rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.cfp-overlay-refresh:hover,
.cfp-overlay-refresh:focus-visible {
  background: #1f2b27;
  border-color: #3e4f49;
}

.cfp-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 1rem;
  background: var(--ink-soft);
  border-top: 1px solid var(--line);
}

.cfp-meta {
  display: flex;
  align-items: baseline;
  gap: 0.65rem;
  min-width: 0;
}

.cfp-title {
  /* A flex item won't shrink below its content's natural width unless its
     min-width is explicitly zeroed — without this, text-overflow:ellipsis
     silently does nothing and long titles just overflow the bar instead. */
  min-width: 0;
  flex: 1 1 auto;
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cfp-live {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--signal);
  flex: none;
}

.cfp-live i {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: var(--signal);
  animation: cfp-pulse 2s ease-in-out infinite;
}

.cfp-waiting {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--chalk-dim);
  flex: none;
}

.cfp-waiting i {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  border: 1px solid var(--chalk-dim);
  animation: cfp-pulse 2s ease-in-out infinite;
}

@keyframes cfp-pulse { 50% { opacity: 0.25; } }

@media (prefers-reduced-motion: reduce) {
  .cfp-live i, .cfp-waiting i { animation: none; }
}

.cfp-action {
  flex: none !important;
  display: inline-block !important;
  padding: 0.5rem 0.9rem !important;
  margin: 0 !important;
  border: 1px solid var(--line) !important;
  border-radius: 6px !important;
  background: transparent !important;
  color: var(--chalk) !important;
  font-size: 0.85rem !important;
  line-height: 1.3 !important;
  font-weight: 600 !important;
  text-decoration: none !important;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.cfp-action:hover,
.cfp-action:focus-visible { background: #1f2b27; border-color: #3e4f49; }

#cf-class-player a:focus-visible,
#cf-recordings a:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
}

.cfp-info {
  margin-top: 1rem;
  padding: 1rem 1.1rem;
  background: var(--ink);
  border: 1px solid var(--line);
  border-radius: 10px;
}

.cfp-info p { margin: 0; }
.cfp-info p + p { margin-top: 0.7rem; }

.cfp-info-name {
  font-size: 1rem;
  line-height: 1.5;
}

.cfp-info-dates,
.cfp-info-schedule {
  font-size: 0.88rem;
  line-height: 1.6;
  color: var(--chalk-dim);
}

.cfp-info-dates strong { color: var(--chalk); font-weight: 600; }

.cfp-help {
  margin: 0.6rem 0 0;
  font-size: 0.78rem;
  color: var(--chalk-dim);
  text-align: right;
}

.cfp-help a { color: var(--chalk-dim); text-decoration: underline; }

@media (max-width: 540px) {
  .cfp-bar { flex-direction: column; align-items: stretch; }
  .cfp-action { text-align: center; }
  .cfp-help { text-align: center; }
}

/* ---------- recordings: card grid + modal ----------
   Tapping a card animates it (shared-layout, via motion) into a modal
   with the list dimmed behind, so what's playing is unmistakable and you
   never have to scroll back anywhere to watch it. */
.cfp-cardgrid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.85rem;
}

@media (min-width: 34rem) {
  .cfp-cardgrid { grid-template-columns: repeat(2, 1fr); }
}

@media (min-width: 52rem) {
  .cfp-cardgrid { grid-template-columns: repeat(3, 1fr); }
}

.cfp-card {
  display: block;
  width: 100%;
  padding: 0;
  margin: 0;
  text-align: left;
  background: var(--ink-soft);
  border: 1px solid var(--line);
  border-radius: 10px;
  overflow: hidden;
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.cfp-card:hover { border-color: #3e4f49; }
.cfp-card:focus-visible { outline: 2px solid var(--focus); outline-offset: 2px; }

.cfp-card-media {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #000;
  overflow: hidden;
}

.cfp-card-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.cfp-card-thumb-placeholder {
  width: 100%;
  height: 100%;
  background: var(--ink-soft);
}

.cfp-card-play {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  pointer-events: none;
  filter: drop-shadow(0 1px 5px rgba(0, 0, 0, 0.7));
}

.cfp-card-info { padding: 0.6rem 0.8rem 0.75rem; }

.cfp-card-title {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  line-height: 1.3;
  color: var(--chalk);
}

.cfp-card-meta {
  margin: 0.15rem 0 0;
  font-size: 0.78rem;
  color: var(--chalk-dim);
}

.cfp-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2147483000;
  background: rgba(0, 0, 0, 0.82);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  overscroll-behavior: contain;
}

.cfp-modal {
  position: relative;
  width: 100%;
  max-width: 56rem;
  background: var(--ink);
  border: 1px solid var(--line);
  border-radius: 12px;
  overflow: hidden;
}

.cfp-modal-media { width: 100%; background: #000; }
.cfp-modal-media .video-js { width: 100% !important; }

.cfp-modal-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.8rem 1rem;
  background: var(--ink-soft);
  border-top: 1px solid var(--line);
}

/* Title and its date/duration stack, so a long title never fights the
   button for horizontal room (which squashed both before). */
.cfp-modal-meta { min-width: 0; flex: 1 1 auto; }

.cfp-modal-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  line-height: 1.3;
  color: var(--chalk);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cfp-modal-sub {
  margin: 0.15rem 0 0;
  font-size: 0.78rem;
  line-height: 1.3;
  color: var(--chalk-dim);
}

/* Icon + label, so it reads as a fullscreen control rather than a bare
   text link. flex:none keeps it from being squeezed by a long title. */
.cfp-action-fs {
  display: inline-flex !important;
  align-items: center;
  gap: 0.4rem;
  flex: none !important;
  white-space: nowrap;
}

.cfp-action-fs svg { display: block; flex: none; }

@media (max-width: 30rem) {
  .cfp-modal-bar { flex-direction: column; align-items: stretch; }
  .cfp-modal-title { white-space: normal; }
  .cfp-action-fs { justify-content: center; }
}

.cfp-modal-close {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  cursor: pointer;
}

.cfp-modal-close:hover { background: rgba(0, 0, 0, 0.85); }
.cfp-modal-close:focus-visible { outline: 2px solid var(--focus); outline-offset: 2px; }

.cfp-row-slot { width: 100%; }
.cfp-row-slot .video-js { width: 100% !important; }

/* Solo view — the destination of a "Watch in Fullscreen" hand-off. Just
   the one class, big, with enough context (course name) that the page
   doesn't read as an orphaned video. No list to scroll here on purpose. */
.cfp-solo-course {
  margin: 0 0 0.5rem;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--chalk-dim);
}

.cfp-solo-slot { width: 100%; background: #000; }
.cfp-solo-slot .video-js { width: 100% !important; }

/* Solo (hand-off) page: one-tap start overlay + "more classes" list */
.cfp-solo-stage { position: relative; }

.cfp-solo-start {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  width: 100%;
  border: none;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font: inherit;
  cursor: pointer;
  z-index: 4;
}

.cfp-solo-start-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.85);
}

.cfp-solo-start-label {
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.cfp-more-head {
  margin: 1.25rem 0 0.6rem;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--chalk-dim);
}

/* Live class card: same card/modal language as recordings */
.cfp-live-card { display: block; width: 100%; }

.cfp-live-badge {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  background: var(--signal);
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
}

.cfp-live-badge i {
  width: 0.4rem;
  height: 0.4rem;
  border-radius: 50%;
  background: #fff;
  animation: cfp-pulse 2s ease-in-out infinite;
}

/* The status overlay sits over the player inside the modal */
.cfp-modal-media { position: relative; }

/* The status overlay (inset:0, z-index:3) sits over the player inside the
   modal — without this the close button ends up underneath it and the
   modal becomes impossible to dismiss whenever an overlay is showing
   (confirmed: pointer events were being intercepted). */
.cfp-modal-close { z-index: 6; }

/* --- modal hardening ---
   The live modal previously bled through: page text underneath stayed
   readable straight through the panel. Two causes, both fixed here — the
   panel needs an explicitly opaque background that Kajabi's page CSS
   can't override, and it needs its own stacking context above the
   backdrop rather than relying on source order. */
.cfp-modal {
  background: var(--ink) !important;
  position: relative;
  z-index: 1;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.65);
}

.cfp-modal-backdrop { background: rgba(0, 0, 0, 0.9) !important; }

.cfp-modal-bar { background: var(--ink-soft) !important; }

/* Live card art — CONFIG.poster is empty, so without this the live card
   is just a blank grey rectangle, which read as "broken" rather than
   "tap to watch". */
.cfp-live-card-media { background: #0a1a14; }

.cfp-live-card-art {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(120% 100% at 50% 0%, rgba(127, 212, 184, 0.18) 0%, rgba(13, 21, 18, 0) 70%),
    linear-gradient(160deg, #12241d 0%, #0d1512 100%);
  color: #fff;
}

/* The modal is portalled to <body> to escape ancestors with transforms /
   filters / will-change, which silently make position:fixed resolve
   against that ancestor instead of the viewport (what pinned it mid-page
   inside the Kajabi app). That also puts it outside #cf-recordings /
   #cf-class-player, where the palette variables live — so they're
   redeclared here, or every colour below would resolve to nothing and the
   panel would render transparent. */
.cfp-modal-backdrop,
.cfp-modal-shell {
  --ink: #0d1512;
  --ink-soft: #16201c;
  --line: #2a3733;
  --chalk: #eef2ef;
  --chalk-dim: #94a49d;
  --signal: #e03d2f;
  --focus: #7fd4b8;
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  color: var(--chalk);
  -webkit-text-size-adjust: 100%;
}

.cfp-modal-backdrop *,
.cfp-modal-shell * { box-sizing: border-box; }

/* Radix Dialog shell — Radix owns portalling/focus/scroll-lock; this just
   positions the panel. Centred via fixed where that's meaningful, or
   absolutely at the tapped card inside the app webview (see
   captureOverlayBox) where fixed has no viewport to resolve against. */
.cfp-modal-shell {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: min(56rem, calc(100% - 2rem));
  z-index: 2147483001;
}

.cfp-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

/* When anchored to a card the shell spans the page width, so centre the
   panel within it. */
.cfp-modal-shell > .cfp-modal { margin: 0 auto; max-width: 56rem; }

/* Inline expansion used inside the app webview, where there is no viewport
   to centre against — the player simply takes the card's place in the
   flow, so it can't be clipped or land off-screen. */
.cfp-inline-panel {
  background: var(--ink);
  border: 1px solid var(--focus);
  border-radius: 10px;
  overflow: hidden;
}

.cfp-inline-panel .cfp-row-slot { width: 100%; background: #000; }
.cfp-inline-panel .video-js { width: 100% !important; }

.cfp-inline-close {
  flex: none;
  padding: 0.5rem 0.9rem;
  border: 1px solid var(--line);
  border-radius: 6px;
  background: transparent;
  color: var(--chalk-dim);
  font: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
}

.cfp-inline-close:hover { background: #1f2b27; color: var(--chalk); }

/* The live badge sat inside a text paragraph as an inline-flex span with a
   trailing space doing the spacing — which collapsed unpredictably next to
   the title. Proper flex row with a real gap instead. */
.cfp-modal-title-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.cfp-live-inline { flex: none; }

.cfp-modal-title-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* In-app "modal": the panel stays in the document flow (so it's always
   where the card was, never off-screen), but everything behind it is
   dimmed by a full-document backdrop, which is what actually makes it
   read as a modal rather than a highlighted list row. */
.cfp-inline-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  background: rgba(0, 0, 0, 0.85);
  z-index: 2147482000;
}

.cfp-inline-panel {
  position: relative;
  z-index: 2147482001;
  border-color: var(--line) !important;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.65);
}

/* With no stream loaded (class not running) the media area had no
   intrinsic height, so the status overlay — absolutely positioned inside
   it — collapsed onto the title bar and the Refresh button jammed into
   the title. Reserve a 16:9 box regardless of whether a video is loaded,
   and have the player fill it rather than relying on video.js's fluid
   padding trick, which only produces height once a source exists. */
.cfp-modal-media,
.cfp-inline-panel .cfp-row-slot {
  position: relative;
  background: #000;
}

/* video.js fluid mode produces the player's height itself, via a
   padding-top percentage. Forcing height:100% + padding-top:0 here made
   that resolve to ZERO inside a grid cell with no fixed height — audible
   video, nothing visible. Leave its sizing alone; only set the width. */
.cfp-modal-media .video-js,
.cfp-inline-panel .video-js {
  width: 100% !important;
}

/* The status overlay can be TALLER than the 16:9 video box (heading +
   body + Refresh on a narrow phone). Absolutely positioned, it added no
   height, so it overflowed — clipping its own heading and dropping the
   Refresh button onto the title bar. Stacking slot and overlay in a
   single grid cell instead means the container takes the height of
   whichever is taller: 16:9 when playing, the overlay's own height when
   there's a message to show. */
.cfp-modal-media,
.cfp-inline-panel .cfp-row-slot {
  display: grid;
  aspect-ratio: auto;
}

.cfp-modal-media > *,
.cfp-inline-panel .cfp-row-slot > * {
  grid-area: 1 / 1;
}

.cfp-modal-media > .cfp-row-slot {
  position: relative;
  inset: auto;
}

.cfp-modal-media > .cfp-overlay {
  position: relative;
  inset: auto;
  min-height: 100%;
  padding: 1.25rem 1rem;
}
